import React, { useCallback, useMemo, useState } from 'react';
import { Row, Col, Button, message, Dropdown, Menu, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatBRNumber, formatCurrency, formatSize } from '@utils/formatters';
import Table from '@components/Table';
import { useNavigate, useParams } from 'react-router-dom';
import useSwr from '@hooks/useSwr';
import api from '@services/api';
import ConsumptionCard from '@components/ConsumptionCard';
import BackToListing from '@components/BackToListing';
import useSearchParams from '@hooks/useSearchParams';
import { InfoCircleOutlined } from '@ant-design/icons';
import InvoiceItems from '../StandardDetails/InvoiceItems';
import InvoiceStats from '../StandardDetails/InvoiceStats';

type ReportTypes =
  | 'RESUMO DA FATURA ABERTA'
  | 'RESUMO DA FATURA'
  | 'CONSUMO DIARIO ABERTA'
  | 'CONSUMO DIARIO'
  | 'CONSOLIDADO ABERTA'
  | 'CONSOLIDADO'
  | 'FATURAMENTO DETALHADO';

const InvoiceDetailed = () => {
  const { status, invoiceId } = useParams();
  const { t, i18n } = useTranslation(['invoices', 'reports']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const { setSearchParams, searchParams } = useSearchParams({
    type: 'pagination',
  });

  const { page, linesPerPage } = useMemo(() => searchParams, [searchParams]);

  const { data: invoiceDetails } = useSwr(
    status === 'opened'
      ? `/service-proxy/opened/invoices/details/${invoiceId}`
      : `/service-proxy/closed/invoices/details/${invoiceId}`,
  );

  const { data: invoiceUsage } = useSwr(
    status === 'opened'
      ? `/service-proxy/opened/invoices/usage/${invoiceId}`
      : `/service-proxy/closed/invoices/usage/${invoiceId}`,
    {
      params: {
        page: page ? Number(page) : 1,
        pageSize: linesPerPage ?? 20,
      },
    },
  );

  const listItems = useMemo(() => invoiceDetails?.listaItens, [invoiceDetails]);

  const consumption = useMemo(() => {
    const balance = invoiceDetails?.dadosSaldo;

    return {
      totalFranchise: balance?.nuFranquiaTotal,
      franchiseConsumption: balance?.nuConsumoFranquia,
      surplusConsumption: balance?.nuConsumoExcedente,
      totalConsumption: balance?.nuConsumoTotal,
    };
  }, [invoiceDetails]);

  const formatValuesWhenExistZero = (value: string) => {
    const splitValues = value.split(',');

    if (splitValues[0] && Number(splitValues[0]) <= 0) {
      return `0 MB`;
    }

    if (splitValues[1] && Number(splitValues[1]) <= 0) {
      return `${splitValues[0]}.00 MB`;
    }

    return `${value.replaceAll(',', '.')} MB`;
  };

  const columnsUsage = useMemo(
    () => [
      { title: 'Consumo', dataIndex: 'dsTipoConsumo' },
      {
        title: 'Data',
        dataIndex: 'dataEvento',
        align: 'center' as const,
        render: (value: string) => value.replaceAll('-', '/'),
      },
      {
        title: 'Franquia',
        dataIndex: 'nuConsumoFranquiaMb',
        render: (value: string) => formatValuesWhenExistZero(value),
      },
      {
        title: 'Excedente',
        dataIndex: 'nuConsumoExcedenteMb',
        render: (value: string) => formatValuesWhenExistZero(value),
      },
      {
        title: 'Saldo',
        dataIndex: 'nuSaldoFranquiaMb',
        render: (value: string) => formatValuesWhenExistZero(value),
      },
    ],
    [],
  );

  const navigateToReports = useCallback(
    (reportType: ReportTypes) => {
      message.destroy('report-requested');
      navigate(`/support/reports?type=${reportType?.toLocaleLowerCase()}`);
    },
    [navigate],
  );

  const handleReports = useCallback(
    (reportType: ReportTypes) => {
      const idFatura = invoiceDetails?.dadosFatura?.id_fatura;

      setLoading(true);

      api
        .post('/report-request/', {
          relatoryType: reportType,
          filter: idFatura,
        })
        .then(() => {
          message.success({
            content: (
              <span>
                {t('reports:messages.request-report-success')}
                {t('reports:messages.check-report-progress-and-download')}
                <Button
                  type="link"
                  onClick={() => navigateToReports(reportType)}
                  style={{ padding: 3 }}
                >
                  {t('reports:messages.go-to-reports')}
                </Button>
              </span>
            ),
            key: 'report-requested',
            duration: 5,
          });
        })
        .catch(() => message.error(t('reports:messages.request-report-error')))
        .finally(() => {
          setLoading(false);
        });
    },
    [invoiceDetails, navigateToReports, t],
  );

  const handleReportsDetail = useCallback(() => {
    const idFatura = invoiceDetails?.dadosFatura?.id_fatura;

    setLoading(true);

    api
      .get(
        `/service-proxy/closed/invoices/detailed-billing?invoiceId=${idFatura}&locale=${i18n.language}`,
      )
      .then(() => {
        message.success({
          content: (
            <span>
              {t('reports:messages.request-report-success')}
              {t('reports:messages.check-report-progress-and-download')}
              <Button
                onClick={() => navigateToReports('FATURAMENTO DETALHADO')}
                type="link"
                style={{ padding: 3 }}
              >
                {t('reports:messages.go-to-reports')}
              </Button>
            </span>
          ),
          key: 'report-requested',
          duration: 5,
        });
      })
      .catch(() => message.error(t('reports:messages.request-report-error')))
      .finally(() => {
        setLoading(false);
      });
  }, [
    i18n.language,
    invoiceDetails?.dadosFatura?.id_fatura,
    navigateToReports,
    t,
  ]);

  const expirationDateFormatted = useMemo(
    () => invoiceDetails?.dadosFatura?.dataVencimento.replaceAll('-', '/'),
    [invoiceDetails],
  );

  const periodDateFormatted = useMemo(
    () =>
      invoiceDetails?.dadosFatura?.dataInicio &&
      invoiceDetails?.dadosFatura?.dataTermino &&
      `${
        invoiceDetails?.dadosFatura?.dataInicio
          .slice(0, 5)
          .replaceAll('-', '/') ?? '-'
      } ${t('detailed.invoice.to')} ${
        invoiceDetails?.dadosFatura?.dataTermino
          .slice(0, 5)
          .replaceAll('-', '/') ?? '-'
      }`,
    [t, invoiceDetails],
  );

  const stats = useMemo(() => {
    const details = invoiceDetails?.dadosFatura;

    return {
      value: details?.nuValorFatura && formatCurrency(details?.nuValorFatura),
      expirationDate: expirationDateFormatted,
      totalTraffic:
        details?.nuTrafegoTotal && formatSize(details?.nuTrafegoTotal),
      period: periodDateFormatted,
      number: details?.nuFaturaKenan || '',
      terminals: details?.nuTerminaisUso,
      chips:
        details?.nuChipsCobrados &&
        `${
          details?.nuChipsCobrados && formatBRNumber(details?.nuChipsCobrados)
        } / ${formatBRNumber(details?.nuChipsMulta) ?? 0}`,
    };
  }, [
    expirationDateFormatted,
    invoiceDetails?.dadosFatura,
    periodDateFormatted,
  ]);

  return (
    <div>
      <BackToListing title={t('cards.details')} />
      <Row gutter={[8, 8]} style={{ padding: 12 }} justify="end">
        <Dropdown
          overlayClassName="overlay-menu"
          placement="bottomRight"
          overlay={
            <Menu>
              <Menu.ItemGroup key="g1">
                <Menu.Item
                  onClick={() => {
                    handleReports(
                      status === 'opened'
                        ? 'RESUMO DA FATURA ABERTA'
                        : 'RESUMO DA FATURA',
                    );
                  }}
                >
                  <span>
                    {t('buttons.detailedReport')}
                    <Tooltip
                      title={t('buttons.messages.detailedReport')}
                      placement="top"
                    >
                      <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                      />
                    </Tooltip>
                  </span>
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    handleReports(
                      status === 'opened'
                        ? 'CONSUMO DIARIO ABERTA'
                        : 'CONSUMO DIARIO',
                    );
                  }}
                >
                  <span>
                    {t('buttons.consumptionReport')}
                    <Tooltip
                      title={t('buttons.messages.consumptionReport')}
                      placement="top"
                    >
                      <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                      />
                    </Tooltip>
                  </span>
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    handleReports(
                      status === 'opened'
                        ? 'CONSOLIDADO ABERTA'
                        : 'CONSOLIDADO',
                    );
                  }}
                >
                  <span>
                    {t('buttons.invoiceConsumptionReport')}
                    <Tooltip
                      title={t('buttons.messages.invoiceConsumptionReport')}
                      placement="top"
                    >
                      <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                      />
                    </Tooltip>
                  </span>
                </Menu.Item>

                {status !== 'opened' && (
                  <Menu.Item onClick={handleReportsDetail}>
                    <span>
                      {t('buttons.detailedInvoice')}
                      <Tooltip
                        title={t('buttons.messages.detailedInvoice')}
                        placement="top"
                      >
                        <Button
                          type="link"
                          size="small"
                          icon={<InfoCircleOutlined />}
                        />
                      </Tooltip>
                    </span>
                  </Menu.Item>
                )}
              </Menu.ItemGroup>
            </Menu>
          }
          trigger={['click']}
        >
          <div>
            <Button type="default" loading={loading}>
              {t('buttons.reports')}
            </Button>
          </div>
        </Dropdown>
      </Row>

      <InvoiceStats data={stats} />

      <Row
        style={{
          margin: '16px 0',
        }}
      >
        <Col lg={24} md={24} xl={24} xs={24}>
          <ConsumptionCard data={consumption} />
        </Col>
      </Row>

      <InvoiceItems
        data={listItems}
        canAddNewItem={false}
        canManageItems={false}
      />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col lg={24} md={24} xl={24} xs={24}>
          <Table
            title={t('table.titles.itemsOfUse')}
            key="invoiceUsage"
            tableKey="invoicesMultiConnectDetailedUsage"
            columns={columnsUsage}
            data={invoiceUsage?.listaItensUso}
            loading={!invoiceUsage?.listaItensUso}
            className="invoices-detailed-table"
            onTableChange={setSearchParams}
            pagination={{
              page,
              linesPerPage,
              total: invoiceUsage?.nuQtdItens,
            }}
          />
        </Col>
      </Row>
    </div>
  );
};

export default InvoiceDetailed;
